import * as React from "react";
import {observer} from "mobx-react";
import {autorun} from "mobx";
import EsriGraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import EsriGraphic from "@arcgis/core/Graphic";
import EsriPoint from "@arcgis/core/geometry/Point";
import {Graphic} from "../../graphic";
import {SymbolConfiguration} from "../../../../config/map-viewer-configuration";
import { planarArea } from "@arcgis/core/geometry/geometryEngine";
import * as reactiveUtils from "@arcgis/core/core/reactiveUtils";
import {AppConfiguration} from "../../../../core/config/app-configuration";
import {MapUtils} from "../../../../utils/map-utils";
import {IAppStore, withStore} from "../../../../core/stores/abstract-app-store";

export interface FeatureHighlightProps {
    appConfig: AppConfiguration;
    symbols: SymbolConfiguration;
    store?: IAppStore; // this is injected by withStore
}

/**
 * Component to draw sketched or API provided features on the map
 */
@observer
class FeatureHighlightBase extends React.Component<FeatureHighlightProps, any> {
    watchHandles: any[] = [];
    autorunDisposers: any[] = [];
    appConfig: AppConfiguration;
    constructor(props: FeatureHighlightProps) {
        super(props);
        this.appConfig = props.appConfig;
    }

    /**
     * Waiting for the map being ready before creating graphic layers.
     * For every graphic layer name in the app store a new graphic layer will be added to the map.
     */
    componentDidMount(): void {
        const {store, symbols} = this.props;
        this.autorunDisposers.push(autorun(() => {
            const {view} = store?.mapStore;
            const {graphics, usedLayers} = store?.appStore;
            if (!view || !view.map || !graphics || graphics.length === 0) {
                return;
            }
            // Remove all graphics from all layers before repainting
            this.clear(usedLayers, view);

            // Add all graphics to the corresponding layer
            graphics.forEach((g: Graphic, index: number) => {
                const layer = this.getLayer(g.layerName, view);
                g.graphic.symbol = this.getSymbol(g.layerName, symbols);
                if (typeof g.graphic.attributes === "undefined" || g.graphic.attributes === null) {
                    g.graphic.attributes = {index};
                } else {
                    g.graphic.attributes.index = index;
                }
                layer.graphics.add(g.graphic);

                const textSymbol = this.getSymbol(g.labelLayerName, symbols);
                if (textSymbol) {
                    const textLayer = this.getLayer(g.labelLayerName, view);
                    const area = planarArea(g.graphic.geometry as __esri.Polygon, "square-meters");
                    textSymbol.text = Intl.NumberFormat("de-CH",
                        {maximumFractionDigits: 2}).format(area / 10000) + " ha";
                    const lblGra = new EsriGraphic();
                    lblGra.geometry = (g.graphic.geometry as __esri.Polygon).centroid;
                    lblGra.symbol = textSymbol;
                    textLayer.visible = true;
                    textLayer.graphics.add(lblGra);
                }
            });

            // Zooming the layers
            this.zoomLayers(view);
        }));
        this.autorunDisposers.push(autorun(() => {
            const {view} = store?.mapStore;
            const {currentLabel} = store?.appStore;
            if (!view || !currentLabel) {
                return;
            }
            // Update all labels to the corresponding graphic
            currentLabel.esriGraphics.forEach((g: __esri.Graphic) => {
                const textSymbol = this.getSymbol(currentLabel.layerName + "_text", symbols);
                const textLayer = this.getLayer(currentLabel.layerName + "_text", view);
                const graphic = textLayer.graphics.getItemAt(g.attributes.index);
                const area = planarArea(g.geometry as __esri.Polygon, "square-meters");
                textSymbol.text = Intl.NumberFormat("de-CH",
                    {maximumFractionDigits: 2}).format(area / 10000) + " ha";
                graphic.symbol = textSymbol;
                graphic.geometry = (g.geometry as __esri.Polygon).centroid;
            });
        }));
    }
    componentWillUnmount(): void {
        this.watchHandles.forEach((handle: any) => {
            handle.remove();
        });
        this.watchHandles.length = 0;
        this.autorunDisposers.forEach((disposer: any) => {
            disposer();
        });
        this.autorunDisposers.length = 0;
    }
    /**
     * Get the symbol configuration per layer name. Return default symbol if not configured.
     *
     * @param layerName: The layer name
     */
    getSymbol(layerName: string, symbols: SymbolConfiguration): any {
        const symbolProps = symbols[layerName];
        if (!symbolProps && layerName.indexOf("_text") <= 0) {
            console.log("No symbology properties found for layer " + layerName);
        }

        return symbolProps;
    }

    /**
     * Remove all graphics on all graphics layers
     *
     * @param layerNames: A list of layer names to clear.
     * @param view: The view to clear the layers.
     */
    clear(layerNames: string[], view: __esri.MapView | __esri.SceneView): void {
        layerNames.forEach((layerName: string) => {
            const layer = view.map.layers.find(
                (l: __esri.GraphicsLayer) => l.title === layerName) as __esri.GraphicsLayer;
            if (layer) {
                layer.removeAll();
            }
        });
    }

    /**
     * Get existing graphics layer by title. The layer will be created if not existing.
     *
     * @param layerName: The unique name of the graphics layer
     * @param view: The view to look for the graphics layer
     */
    getLayer(layerName: string, view: __esri.MapView | __esri.SceneView): __esri.GraphicsLayer {
        let layer = view.map.layers.find((l: __esri.GraphicsLayer) => l.title === layerName) as __esri.GraphicsLayer;
        if (!layer) {
            layer = new EsriGraphicsLayer({listMode: "hide", title: layerName});
            view.map.layers.push(layer);
        }
        return layer;
    }

    /**
     * Zoom features within all graphics layers in app store.
     * Esri doesn't provide a method to zoom the whole extent of multiple graphics layers at once.
     * The idea here is to produce extent graphics for every graphics layers and zoom the list of extents
     *
     * @param view: The map view or the scene view to calculate the zoom extent
     */
    zoomLayers(view: __esri.MapView | __esri.SceneView): void {
        const geometries: __esri.Geometry[] = [];
        view.map.layers.forEach((layer: __esri.GraphicsLayer) => {
            if (layer.graphics) {
                layer.graphics.forEach((g: __esri.Graphic) => {
                    const graphicClone = g.geometry.clone();
                    geometries.push(graphicClone);
                });
            }
        });
        // Der Extent in der View-Konfiguration setzten, damit dieser nicht etwa den Extent der "Graphic" überschreibt
        if(!this.appConfig.mapView?.extent){
            if(this.appConfig.mapView.viewpoint){
                delete this.appConfig.mapView.viewpoint;
            }
            if(this.appConfig.mapView.center){
                delete this.appConfig.mapView.center;

            }
            this.appConfig.mapView.extent = MapUtils.calculateMinExtent(geometries, 50).toJSON();
        }
        // Zoom graphics
        this.watchHandles.push(reactiveUtils.when(
            () => view.ready,
            () => {
                view.goTo(geometries).catch((error) => {
                    if (error.name !== "view:goto-interrupted") {
                        // eslint-disable-next-line no-console
                        console.error(error);
                    }
                });
            },
            { initial: true, once: true }
        ));
    }

    render(): JSX.Element | null {
        return null;
    }
}
export const FeatureHighlight = withStore(FeatureHighlightBase);
